import React, { useState, useRef, useEffect } from 'react';
import { Mail, Bell, Zap, Image, Paperclip, Variable, Upload, Info, Loader2, CheckCircle, AlertCircle, Save } from 'lucide-react';
import useInstructorMarketingStore from '@/stores/InstructorStore/InstructorMarketingStore';
import { Audience, CampaignData } from '@/services/InstructorService/InstructorMarketingService';
import './ComposeNotification.css';

const ComposeNotification: React.FC = () => {
  const { audiences: storeAudiences, createAndSendCampaign, sending, error, resetError } = useInstructorMarketingStore();

  const [notificationTitle, setNotificationTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'email' | 'in-app' | 'both'>('email');
  const [audiences, setAudiences] = useState<Audience[]>(storeAudiences || []);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  // Current local date-time as YYYY-MM-DDTHH:mm, used as the min for scheduling.
  const minDateTime = (() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync store data to local state
  useEffect(() => {
    if (storeAudiences) {
      setAudiences(storeAudiences);
    }
  }, [storeAudiences]);

  // Auto-hide feedback after 5 seconds
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const handleAudienceToggle = (id: string) => {
    setAudiences(audiences.map(aud =>
      aud.id === id ? { ...aud, checked: !aud.checked } : aud
    ));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        alert('Only CSV files are supported');
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        alert('Only CSV files are supported');
        return;
      }
      setUploadedFile(file);
    }
  };

  const selectedCount = audiences.filter(a => a.checked).length;

  const resetForm = () => {
    setNotificationTitle('');
    setMessage('');
    setNotificationType('email');
    setAudiences(audiences.map(a => ({ ...a, checked: false })));
    setUploadedFile(null);
    setScheduleType('now');
    setScheduleDate('');
  };

  const handleSaveAsDraft = async () => {
    // Reset any previous errors
    if (error) resetError();

    // Validation
    if (!notificationTitle.trim()) {
      setFeedback({ type: 'error', message: 'Please enter a notification title' });
      return;
    }
    if (!message.trim()) {
      setFeedback({ type: 'error', message: 'Please enter a message' });
      return;
    }
    if (selectedCount === 0) {
      setFeedback({ type: 'error', message: 'Please select at least one audience' });
      return;
    }

    setIsSavingDraft(true);

    try {
      // Build campaign data for draft
      const selectedRoles = audiences
        .filter(a => a.checked && a.role)
        .map(a => a.role)
        .filter((role): role is number => role !== undefined);

      const campaignData: CampaignData = {
        title: notificationTitle.trim(),
        subject: notificationTitle.trim(),
        message: message.trim(),
        targetAudience: {
          roles: selectedRoles.length > 0 ? selectedRoles : undefined,
          departments: undefined,
          specificUsers: undefined
        },
        deliveryMethod: notificationType,
        scheduledFor: null
      };

      // Save as draft (sendImmediately = false)
      const result = await createAndSendCampaign(campaignData, false);

      if (result.success) {
        setFeedback({ type: 'success', message: 'Notification saved as draft successfully!' });
        resetForm();
      } else {
        setFeedback({ type: 'error', message: result.error || 'Failed to save draft' });
      }
    } catch (error) {
      setFeedback({ type: 'error', message: 'An error occurred while saving draft' });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSend = async () => {
    // Reset any previous errors
    if (error) resetError();

    // Validation
    if (!notificationTitle.trim()) {
      alert('Please enter a notification title');
      return;
    }
    if (!message.trim()) {
      alert('Please enter a message');
      return;
    }
    if (selectedCount === 0) {
      alert('Please select at least one audience');
      return;
    }
    if (scheduleType === 'schedule' && !scheduleDate) {
      alert('Please select a date for scheduled sending');
      return;
    }
    if (scheduleType === 'schedule' && scheduleDate < minDateTime) {
      alert('Schedule date and time cannot be in the past');
      return;
    }

    // Build campaign data
    const selectedRoles = audiences
      .filter(a => a.checked && a.role)
      .map(a => a.role)
      .filter((role): role is number => role !== undefined);

    const campaignData: CampaignData = {
      title: notificationTitle,
      subject: notificationTitle,
      message,
      targetAudience: {
        roles: selectedRoles.length > 0 ? selectedRoles : undefined,
        departments: undefined,
        specificUsers: undefined
      },
      deliveryMethod: notificationType,
      scheduledFor: scheduleType === 'schedule' ? scheduleDate.replace('T', ' ') : null
    };

    // Send campaign
    const result = await createAndSendCampaign(campaignData, scheduleType === 'now');

    if (result.success) {
      alert('Campaign sent successfully!');
      // Reset form
      setNotificationTitle('');
      setMessage('');
      setNotificationType('email');
      setAudiences(audiences.map(a => ({ ...a, checked: false })));
      setUploadedFile(null);
      setScheduleType('now');
      setScheduleDate('');
    } else {
      alert(`Error: ${result.error || 'Failed to send campaign'}`);
    }
  };

  return (
    <div className="trainer-marketing-compose-content">
      <h2 className="trainer-marketing-section-title">Compose Notification</h2>
      <p className="trainer-marketing-section-subtitle">Create and send notifications to your users</p>

      <div className="trainer-marketing-compose-layout">
        <div className="left-panel">
          <div className="trainer-marketing-compose-card">
            <h3 className="trainer-marketing-card-title">Message Details</h3>

            <div className="trainer-marketing-form-group">
              <label className="trainer-marketing-form-label">Notification Title</label>
              <input
                type="text"
                className="trainer-marketing-form-input"
                placeholder="e.g., New Course Available"
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
              />
            </div>

            <div className="trainer-marketing-form-group">
              <label className="trainer-marketing-form-label">Message</label>
              <textarea
                className="trainer-marketing-form-textarea"
                placeholder="Write your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>

            <div className="trainer-marketing-toolbar">
              <button className="trainer-marketing-toolbar-btn" title="Add Image">
                <Image size={16} />
                <span>Image</span>
              </button>
              <button className="trainer-marketing-toolbar-btn" title="Attach File">
                <Paperclip size={16} />
                <span>Attach</span>
              </button>
              <button className="trainer-marketing-toolbar-btn" title="Add Variable">
                <Variable size={16} />
                <span>Variable</span>
              </button>
            </div>
          </div>

          <div className="trainer-marketing-compose-card">
            <h3 className="trainer-marketing-card-title">Delivery Settings</h3>

            <div className="trainer-marketing-form-group">
              <label className="trainer-marketing-form-label">Notification Type</label>
              <div className="trainer-marketing-notification-types">
                <button
                  className={`trainer-marketing-type-btn ${notificationType === 'email' ? 'trainer-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('email')}
                >
                  <Mail size={20} />
                  <span>Email</span>
                </button>
                <button
                  className={`trainer-marketing-type-btn ${notificationType === 'in-app' ? 'trainer-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('in-app')}
                >
                  <Bell size={20} />
                  <span>In-App</span>
                </button>
                <button
                  className={`trainer-marketing-type-btn ${notificationType === 'both' ? 'trainer-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('both')}
                >
                  <Zap size={20} />
                  <span>Both</span>
                </button>
              </div>
            </div>

            <div className="trainer-marketing-form-group">
              <label className="trainer-marketing-form-label">Select Audience</label>
              <div className="trainer-marketing-audience-list">
                {audiences.map(audience => (
                  <label key={audience.id} className="trainer-marketing-audience-item">
                    <input
                      type="checkbox"
                      checked={audience.checked}
                      onChange={() => handleAudienceToggle(audience.id)}
                      className="trainer-marketing-audience-checkbox"
                    />
                    <div className="trainer-marketing-audience-info">
                      <span className="trainer-marketing-audience-name">{audience.name}</span>
                      <span className="trainer-marketing-audience-count">{audience.userCount} users</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="trainer-marketing-form-group">
              <div
                className="trainer-marketing-upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="trainer-marketing-upload-icon">
                  <Upload size={24} />
                </div>
                <p className="trainer-marketing-upload-text">
                  {uploadedFile ? uploadedFile.name : 'Drop your CSV file here'}
                </p>
                <p className="trainer-marketing-upload-subtext">
                  or click to browse from your computer
                </p>
                <button
                  type="button"
                  className="trainer-marketing-upload-btn"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  Select File
                </button>
                <p className="trainer-marketing-upload-hint">Supports .csv files up to 10MB</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            <div className="trainer-marketing-form-row">
              <div className="trainer-marketing-form-group trainer-marketing-form-group-half">
                <label className="trainer-marketing-form-label">Schedule</label>
                <select
                  className="trainer-marketing-form-select"
                  value={scheduleType}
                  onChange={(e) => setScheduleType(e.target.value)}
                >
                  <option value="now">Send Now</option>
                  <option value="schedule">Schedule</option>
                </select>
              </div>
              <div className="trainer-marketing-form-group trainer-marketing-form-group-half">
                <label className="trainer-marketing-form-label">Date & Time *</label>
                <input
                  type="datetime-local"
                  className="trainer-marketing-form-input"
                  value={scheduleDate}
                  min={minDateTime}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  disabled={scheduleType === 'now'}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="right-panel">
          <div className="trainer-marketing-preview-card">
            <div className="trainer-marketing-preview-header">
              <Mail size={16} className="trainer-marketing-preview-icon" />
              <span>Email Preview</span>
            </div>

            <div className="trainer-marketing-preview-content">
              <div className="trainer-marketing-preview-from">
                <span className="trainer-marketing-preview-label">From:</span>
                <span>noreply@company.com</span>
              </div>
              <div className="trainer-marketing-preview-to">
                <span className="trainer-marketing-preview-label">To:</span>
                <span>Select audience</span>
              </div>

              <div className="trainer-marketing-preview-message">
                <h3 className="trainer-marketing-preview-title">
                  {notificationTitle || 'Your notification title'}
                </h3>
                <p className="trainer-marketing-preview-text">
                  {message || 'Your message will appear here...'}
                </p>
              </div>
            </div>

            <div className="trainer-marketing-ready-banner">
              <Info size={16} />
              <div className="trainer-marketing-ready-content">
                <span className="trainer-marketing-ready-title">Ready to Send</span>
                <ul className="trainer-marketing-ready-list">
                  <li>• {selectedCount} audience{selectedCount !== 1 ? 's' : ''} selected</li>
                  <li>• Type: {notificationType}</li>
                  <li>• {scheduleType === 'now' ? 'Send immediately' : `Scheduled for ${scheduleDate}`}</li>
                </ul>
              </div>
            </div>

            {feedback && (
              <div className={`trainer-marketing-feedback trainer-marketing-feedback-${feedback.type}`}>
                {feedback.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                <span>{feedback.message}</span>
              </div>
            )}

            <div className="trainer-marketing-actions">
              <button
                className="trainer-marketing-draft-btn"
                onClick={handleSaveAsDraft}
                disabled={isSavingDraft || sending}
              >
                {isSavingDraft ? (
                  <>
                    <Loader2 size={18} className="trainer-marketing-spinner" />
                    Saving Draft...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save as Draft
                  </>
                )}
              </button>
              <button
                className="trainer-marketing-send-btn"
                onClick={handleSend}
                disabled={sending || isSavingDraft}
              >
                {sending ? (
                  <>
                    <Loader2 size={18} className="trainer-marketing-spinner" />
                    {scheduleType === 'now' ? 'Sending...' : 'Scheduling...'}
                  </>
                ) : (
                  <>
                    <Mail size={18} />
                    {scheduleType === 'now' ? 'Send Notification' : 'Schedule Notification'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComposeNotification;
